
import { apiMiddleware } from './apiMiddleware'

export interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  phone?: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  role?: string
}

export interface CreateUserData {
  first_name: string
  last_name: string
  email: string
  phone?: string
  password: string
  is_staff?: boolean
  is_superuser?: boolean
  is_active?: boolean
}

export interface UpdateUserData {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  is_staff?: boolean
  is_superuser?: boolean
  is_active?: boolean
}

export interface UsersResponse {
  users: User[]
  users_count: number
  offset: number | null
}

export interface ApiError {
  error: boolean
  errors?: any
  message?: string
}

export interface ApiSuccess {
  error: boolean
  message: string
}

class UsersApiService {
  // No need for baseUrl or makeRequest - using middleware

  async getUser(userId: string): Promise<User> {
    const response = await apiMiddleware.get<{ user: User }>(`users/${userId}/`)
    return response.user
  }

  async getUsers(limit: number = 50, offset: number = 0, search?: string, role?: string, status?: string): Promise<UsersResponse> {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())
    params.append('offset', offset.toString())
    params.append('is_staff', 'true')  
    
    if (search) params.append('search', search)
    if (role) params.append('role', role)
    if (status) params.append('status', status)

    return apiMiddleware.get<UsersResponse>(`users/?${params.toString()}`)
  }

  async getUserById(userId: string): Promise<User> {
    const response = await apiMiddleware.get<{ user: User }>(`users/${userId}/`)
    return response.user
  }

  async createUser(userData: CreateUserData): Promise<ApiSuccess> {
    return apiMiddleware.post<ApiSuccess>('admin-users/', userData)
  }

  async updateUser(userId: string, userData: UpdateUserData): Promise<{ user: User }> {
    return apiMiddleware.put<{ user: User }>(`users/${userId}/`, userData)
  }

  async deleteUser(userId: string): Promise<ApiSuccess> {
    return apiMiddleware.delete<ApiSuccess>(`users/${userId}/`)
  }

  async checkEmailAvailability(email: string, excludeUserId?: string): Promise<{ available: boolean; message: string }> {
    const data: any = { email }
    if (excludeUserId) {
      data.exclude_user_id = excludeUserId
    }
    
    return apiMiddleware.post<{ available: boolean; message: string }>('users/check-email/', data)
  }

  async checkPhoneAvailability(phone: string, excludeUserId?: string): Promise<{ available: boolean; message: string }> {
    const data: any = { phone }
    if (excludeUserId) {
      data.exclude_user_id = excludeUserId
    }
    
    return apiMiddleware.post<{ available: boolean; message: string }>('users/check-phone/', data)
  }
}

// Export a singleton instance
export const usersApi = new UsersApiService() 